import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, TrendingUp, Target, Download } from "lucide-react";

export function FeaturesSection({ features, t }) {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-white">{t("everythingYouNeed")}</h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">{t("featuresDescription")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card key={feature.title} className="bg-zinc-950/50 border-zinc-800/50 card-hover" data-testid={`feature-card-${index}`}>
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-400">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorksSection({ steps, t }) {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-zinc-950/50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-white">{t("howItWorks")}</h2>
          <p className="text-zinc-400">{t("fourSimpleSteps")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={step.number} className="relative" data-testid={`step-${index + 1}`}>
              <div className="text-6xl font-bold text-cyan-500/20 mb-4">{step.number}</div>
              <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
              <p className="text-zinc-400 text-sm">{step.description}</p>
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 right-0 w-full h-[2px] bg-gradient-to-r from-cyan-500/30 to-transparent" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TrustSection({ t }) {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-zinc-950/50">
      <div className="max-w-4xl mx-auto">
        <Card className="bg-gradient-to-br from-zinc-900 to-black border-cyan-500/20 overflow-hidden">
          <CardContent className="p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <ShieldCheck className="w-10 h-10 text-cyan-400" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-white mb-3">{t("researchBased")}</h3>
                <p className="text-zinc-400 mb-4">{t("researchBasedDesc")}</p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-sm text-cyan-400">
                    <TrendingUp className="w-4 h-4" />
                    <span>{t("evidenceBased")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-cyan-400">
                    <Target className="w-4 h-4" />
                    <span>{t("goalSpecific")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-cyan-400">
                    <Download className="w-4 h-4" />
                    <span>{t("exportablePlans")}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
